import {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';

import { FormBuilder, FormBuilderService } from 'thrive-forms';
import { useObservable } from 'thrive-observable-react';
import Dropdown from "@/components/elements/global/Dropdown.tsx";
import CancelConfirmModal from "thrive-components/components/global/modals/CancelConfirmModal.tsx";

import '../../css/portal/formbuilderportal.scss'
import {getAssetById} from "@/api/assets";

export interface IMediaLibaryAsset {
    title: string
    assetId: number,
    url: string
}

function getTextBoxStringFromValue(v: 'poll' | 'none') {
    switch (v) {
        case 'poll':
            return 'Quick Poll'

        case 'none':
        default:
            return 'Custom Form'
    }
}


const makeMediaPicker = (fb: FormBuilderService) =>
    (callback: (data: IMediaLibaryAsset) => void) => {
        (window as any).globalStore.setStatus('formBuilder');
        (window as any).openMediaLibrary('image', false);
        document.body.style.overflow = 'initial';
        (window as any).functions = {
            ...window.functions,
            checkFormIsValidAndCleanAllElementsBeforeSubmit: fb.checkFormIsValidAndCleanAllElementsBeforeSubmit,
        };

        const handler = (e: any) => {
            callback(e.detail);
            document.removeEventListener('itemSelectedFromMediaLibrary', handler);
        };
        document.addEventListener('itemSelectedFromMediaLibrary', handler);
    };

const initialiseFormBuilderServiceFromListPage = () => {
    const FBService = new FormBuilderService();
    FBService.config.handleExternalMediaHandler = makeMediaPicker(FBService);
    FBService.config.getAssetDetailsFromMediaLibrary = getAssetById;
    const initialValue = (document.querySelector('#FormJson') as HTMLTextAreaElement)?.value;

    const title = (document.querySelector('#Title') as HTMLInputElement)?.value;

    const viewFormText = (document.querySelector('#ViewFormButtonText') as HTMLInputElement)?.value;

    if(initialValue) {
        const json = JSON.parse(initialValue);
        FBService.settings.setFormTitle(title || json.title);
        FBService.elements.setFormBuilderData(json.elements);
        FBService.settings.setFormSubmitButtonText(json.submitButtonText)
        FBService.email.setEmailResponseEnabled(json.EmailResponseEnabled)
        FBService.email.setEmailRecipientsList(json.EmailRecipientsList)
        FBService.email.setConditionalEmailRules(json.conditionalEmailRules ?? [], true)
        FBService.settings.setViewFormButtonText(viewFormText || 'View Form');
        FBService.elements.setFormTemplate(json.formTemplate ?? 'none');
        FBService.config.initialiseConfig(json.formTemplate ?? 'none');
    }
    return FBService;
};


const useObserveForm = (setShowFormResponseArea: (val: boolean) => void, shouldRun: boolean) => {
    useEffect(() => {
        if(shouldRun) {
            ;(document?.querySelector('#formlistradio') as HTMLInputElement)?.addEventListener('change', () => {
                setShowFormResponseArea(true);
            });

            ['#defaultlistradio', '#userlistradio', '#recognitionlistradio'].forEach(selector => {
                ;(document?.querySelector(selector) as HTMLInputElement)?.addEventListener('change', () => {
                    setShowFormResponseArea(false);
                })
            })
        }
    }, [shouldRun])
}

const useWriteToFormJson = ({FBService}: {FBService: FormBuilderService}) => {

    const formBuilderData = useObservable<any[]>(FBService.formBuilderData);
    const formBuilderButtonText = useObservable<string>(FBService.formSubmitButtonText)
    const formTitle = useObservable<string>(FBService.formTitle)
    const viewFormButtonText = useObservable(FBService.viewFormButtonText);
    const FormTemplate = useObservable(FBService.elements.FormTemplate);
    const hideSubmitButton = useObservable(FBService.config.hideSubmitButton);
    const formResponseMode = useObservable(FBService.config.formResponseMode);
    const formBuilderEmailResponseEnabled = useObservable<boolean>(FBService.emailResponseEnabled);
    const formBuilderEmailRecipientsList = useObservable<string[]>(FBService.emailRecipientsList);
    const conditionalEmailRules= useObservable<any[]>(FBService.conditionalEmailRules);

    useEffect(() => {
        if (document.querySelector("#FormJson") as HTMLTextAreaElement) {
            ;(document.querySelector("#FormJson") as HTMLTextAreaElement).value = JSON.stringify({
                formType: "form",
                formTemplate: FormTemplate,
                title: formTitle,
                elements: formBuilderData,
                submitButtonText: formBuilderButtonText,
                EmailResponseEnabled: formBuilderEmailResponseEnabled,
                EmailRecipientsList: formBuilderEmailRecipientsList,
                conditionalEmailRules: conditionalEmailRules,
                hideSubmitButton,
                formResponseMode,
            });
        }
        if(document.querySelector('#ViewFormButtonText')) {
            ;(document.querySelector('#ViewFormButtonText') as HTMLInputElement).value = viewFormButtonText
        }

    }, [
        formBuilderData,
        formBuilderButtonText,
        formTitle,
        formBuilderEmailResponseEnabled,
        formBuilderEmailRecipientsList,
        conditionalEmailRules,
        viewFormButtonText,
        FormTemplate
    ])

}

const FormBuilderPortal = () =>  {
        const [FBService] = useState(initialiseFormBuilderServiceFromListPage);

        const [formRadioIsChecked, setFormRadioIsChecked] = useState(false);

        const [showModal, setShowModal] = useState(false);
        const [modalChangeValue, setModalChangeValue] = useState('');

        const [dropdownKey, setDropdownKey] = useState<number>(0);


        const FormTemplate = useObservable(FBService.elements.FormTemplate);

        const listTitleObserver = new MutationObserver((mutation: any[]) => FBService.settings.setFormTitle(mutation[0].target.value));

        useEffect(() => {
            (window as any).functions = {...window.functions, checkFormIsValidAndCleanAllElementsBeforeSubmit: FBService.checkFormIsValidAndCleanAllElementsBeforeSubmit}
        }, [])


        useEffect(() => {

                const formListRadio = document.querySelector('#formlistradio') as HTMLInputElement;
                if(formListRadio) {
                    setFormRadioIsChecked(formListRadio.checked)
                }
        }, []);

        useEffect(() => {
                /* hidden read only id field exists on page, because its read only we cannot bind an on change event like you would expect, so we have to use a mutation observer
                  this will update when a content id is set on save or save and publish
                */
                if(document.querySelector('#Title')) {
                    let element : HTMLInputElement = document?.querySelector?.('#Title')!;
                    listTitleObserver.observe(element as HTMLElement, {attributes: true, childList: true})
                    FBService.settings.setFormTitle(element.value)
                }
        }, []);


        let targetContainer = document.getElementById('react-form-builder-container');
        let controlsContainer = document.getElementById('form-template-select-portal');

        useObserveForm(setFormRadioIsChecked, !!targetContainer)
        useWriteToFormJson({FBService})

        if (!targetContainer) {
                //if there is nowhere for the portal, do not display
                return null;
        } else {

                return (
                    <div className={'formbuilder-portal'}>
                        {ReactDOM.createPortal(
                            <FormBuilder service={FBService}/>,
                            targetContainer
                        )}
                        {formRadioIsChecked && ReactDOM.createPortal(
                            <Dropdown
                                key={dropdownKey}
                                setValue={(v: 'poll' | 'none') => {
                                    if(v !== FormTemplate) {
                                        setModalChangeValue(v);
                                        setShowModal(true);
                                    }

                                }}
                                options={[{value: 'none', label: 'Custom Form'},{value: 'poll', label: 'Quick Poll'}]}
                                defaultValue={getTextBoxStringFromValue(FormTemplate)}
                                placeholder={getTextBoxStringFromValue(FormTemplate)}
                            />,
                            controlsContainer as HTMLDivElement,
                        )}
                        {showModal && <CancelConfirmModal
                            className={'switch-template-alert-modal'}
                            isAlertStyle
                            headerContent={<h3 className={'switch-template-alert-modal-header'}>Switch to {getTextBoxStringFromValue(modalChangeValue as any)}?</h3>}
                            bodyContent={<p className={'switch-template-alert-modal-body'}>Switching will remove all content you’ve added to this {getTextBoxStringFromValue(FormTemplate as any)}. This can’t be undone.</p>}
                            onCloseClick={() => {
                                setShowModal(false);
                                setModalChangeValue('');
                                setDropdownKey(k => k + 1); // remount so the dropdown resets to FormTemplate
                            }}
                            cancelButtonText={'Cancel'}
                            cancelButtonAriaLabel={'cancel'}
                            confirmButtonText={'Confirm'}
                            confirmButtonAriaLabel={'confirm'}
                            onCancelClick={() => {
                                setShowModal(false);
                                setModalChangeValue('');
                                setDropdownKey(k => k + 1); // remount so the dropdown resets to FormTemplate
                            }}
                            onConfirmClick={() => {
                                FBService.elements.applyFormTemplate(modalChangeValue as any, FBService);
                                setShowModal(false);
                                setModalChangeValue('');
                            }}
                        />}
                    </div>

                );
        }
} 

export default FormBuilderPortal;